import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  useTheme,
  alpha,
  Avatar,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Shield as ShieldIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Warning as WarningIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  FiberNew as FiberNewIcon
} from '@mui/icons-material';
import { supabase } from '../../lib/supabaseClient';
import { getAuthUser } from '../../services/authService';
import { apiPost } from '../../lib/apiClient';
import { validatePassword, getPasswordRequirements } from '../../lib/passwordValidation';
import { getUserEntities } from '../../services/userEntityService';

interface Entity {
  id: string;
  name: string;
  subdomain: string;
  logo: string | null;
  created_at: string;
  is_active: boolean;
  features?: {
    resumeScreening?: boolean;
    analytics?: boolean;
    callInitiation?: boolean;
    customThemes?: boolean;
  };
}

interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  entity_id: string;
  entity_name?: string;
  entity_names?: string[];
  entity_ids?: string[];
  is_super_admin: boolean;
  created_at: string;
}

export const SuperAdminPage: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState(0);

  // Listen to navigation item clicks to switch tabs
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab) {
      const tabNum = parseInt(tab);
      if (!isNaN(tabNum) && tabNum >= 0 && tabNum <= 2) {
        setCurrentTab(tabNum);
        return;
      }
      // Legacy string-based tab names
      if (tab === 'users') {
        setCurrentTab(1);
      } else if (tab === 'features') {
        setCurrentTab(2);
      }
    } else {
      setCurrentTab(0); // Entities tab by default
    }
  }, [location.search]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Platform-wide statistics
  const [platformStats, setPlatformStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    newApplications: 0,
    conversionRate: 0,
    avgDaysToHire: 0
  });

  // Entity dialog
  const [entityDialogOpen, setEntityDialogOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<Entity | null>(null);
  const [entityForm, setEntityForm] = useState({
    name: '',
    subdomain: '',
    logo: ''
  });

  // User dialog
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState({
    email: '',
    full_name: '',
    role: 'recruiter',
    entity_ids: [] as string[],
    is_super_admin: false,
    password: '',
    is_sso_user: false
  });

  // Delete confirmation dialogs
  const [deleteEntityDialogOpen, setDeleteEntityDialogOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState<Entity | null>(null);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    checkSuperAdmin();
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      loadEntities();
      loadUsers();
      loadPlatformStats();
    }
  }, [isSuperAdmin]);

  const checkSuperAdmin = async () => {
    try {
      const user = await getAuthUser();
      if (!user) {
        setError('You must be logged in');
        setLoading(false);
        return;
      }

      // Check if user is super admin
      const { data, error: fetchError } = await supabase
        .from('users')
        .select('is_super_admin')
        .eq('id', user.id)
        .single();

      if (fetchError) throw fetchError;

      if (!data?.is_super_admin) {
        setError('Access denied. Super admin privileges required.');
        setLoading(false);
        return;
      }

      setIsSuperAdmin(true);
    } catch (err: any) {
      console.error('Error checking super admin:', err);
      setError('Failed to verify permissions');
    } finally {
      setLoading(false);
    }
  };

  const loadEntities = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('entities')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setEntities(data || []);
    } catch (err: any) {
      console.error('Error loading entities:', err);
      setError('Failed to load entities');
    }
  };

  const loadUsers = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('users')
        .select(`
          *,
          entity:entities!users_entity_id_fkey(name)
        `)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Fetch user_entities for all users to show multi-entity assignments
      let userEntitiesMap: Record<string, { entity_ids: string[]; entity_names: string[] }> = {};
      try {
        const { data: ueData } = await supabase
          .from('user_entities')
          .select('user_id, entity_id, entities(name)');

        if (ueData) {
          for (const ue of ueData) {
            const uid = (ue as any).user_id;
            if (!userEntitiesMap[uid]) {
              userEntitiesMap[uid] = { entity_ids: [], entity_names: [] };
            }
            userEntitiesMap[uid].entity_ids.push((ue as any).entity_id);
            userEntitiesMap[uid].entity_names.push((ue as any).entities?.name || 'Unknown');
          }
        }
      } catch {
        // user_entities table may not exist yet
      }

      const formattedUsers = (data || []).map((user: any) => ({
        ...user,
        full_name: user.name, // Map 'name' column to 'full_name' for frontend
        entity_name: user.entity?.name,
        entity_ids: userEntitiesMap[user.id]?.entity_ids || (user.entity_id ? [user.entity_id] : []),
        entity_names: userEntitiesMap[user.id]?.entity_names || (user.entity?.name ? [user.entity.name] : []),
      }));

      setUsers(formattedUsers);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    }
  };

  const loadPlatformStats = async () => {
    try {
      // Fetch total jobs count
      const { count: totalJobs, error: jobsError } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true });

      if (jobsError) throw jobsError;

      // Fetch active jobs count
      const { count: activeJobs, error: activeJobsError } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      if (activeJobsError) throw activeJobsError;

      // Fetch total applications count
      const { count: totalApplications, error: appsError } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true });

      if (appsError) throw appsError;

      // Fetch new applications (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const { count: newApplications, error: newAppsError } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .gte('submitted_at', sevenDaysAgo.toISOString());

      if (newAppsError) throw newAppsError;

      // Fetch hired applications for conversion rate
      const { count: hiredCount, error: hiredError } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'hired');

      if (hiredError) throw hiredError;

      // Calculate conversion rate
      const conversionRate = totalApplications && totalApplications > 0
        ? Math.round((hiredCount || 0) / totalApplications * 100)
        : 0;

      // Calculate average days to hire
      const { data: hiredApps, error: hiredAppsError } = await supabase
        .from('applications')
        .select('submitted_at, updated_at')
        .eq('status', 'hired')
        .not('updated_at', 'is', null);

      if (hiredAppsError) throw hiredAppsError;

      let avgDaysToHire = 0;
      if (hiredApps && hiredApps.length > 0) {
        const totalDays = hiredApps.reduce((acc, app) => {
          const submitted = new Date(app.submitted_at);
          const updated = new Date(app.updated_at);
          const days = Math.ceil((updated.getTime() - submitted.getTime()) / (1000 * 60 * 60 * 24));
          return acc + days;
        }, 0);
        avgDaysToHire = Math.round(totalDays / hiredApps.length);
      }

      setPlatformStats({
        totalJobs: totalJobs || 0,
        activeJobs: activeJobs || 0,
        totalApplications: totalApplications || 0,
        newApplications: newApplications || 0,
        conversionRate,
        avgDaysToHire
      });
    } catch (err: any) {
      console.error('Error loading platform stats:', err);
      // Don't set error - stats are non-critical
    }
  };

  const handleCreateEntity = () => {
    setEditingEntity(null);
    setEntityForm({ name: '', subdomain: '', logo: '' });
    setEntityDialogOpen(true);
  };

  const handleEditEntity = (entity: Entity) => {
    setEditingEntity(entity);
    setEntityForm({
      name: entity.name,
      subdomain: entity.subdomain,
      logo: entity.logo || ''
    });
    setEntityDialogOpen(true);
  };

  const handleSaveEntity = async () => {
    try {
      setError(null);

      if (!entityForm.name || !entityForm.subdomain) {
        setError('Name and subdomain are required');
        return;
      }

      if (editingEntity) {
        // Update existing entity
        const { error: updateError } = await supabase
          .from('entities')
          .update({
            name: entityForm.name,
            subdomain: entityForm.subdomain,
            logo: entityForm.logo || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingEntity.id);

        if (updateError) throw updateError;
        setSuccess('Entity updated successfully');
        setEntityDialogOpen(false);
        loadEntities();
      } else {
        // Create new entity via API
        const data = await apiPost('/api/admin', {
          action: 'createEntity',
          name: entityForm.name,
          subdomain: entityForm.subdomain,
          logo: entityForm.logo || null,
          is_active: true
        });

        setSuccess(data.message || 'Entity created successfully');
        setEntityDialogOpen(false);
        loadEntities();
      }
    } catch (err: any) {
      console.error('Error saving entity:', err);
      setError(err.message || 'Failed to save entity');
    }
  };

  const handleToggleFeature = async (
    entityId: string,
    featureName: keyof NonNullable<Entity['features']>,
    enabled: boolean
  ) => {
    try {
      setError(null);

      // Get current features
      const entity = entities.find(e => e.id === entityId);
      if (!entity) return;

      const updatedFeatures = {
        ...entity.features,
        [featureName]: enabled
      };

      // Optimistically update local state for immediate UI feedback
      setEntities(prev => prev.map(e =>
        e.id === entityId
          ? { ...e, features: updatedFeatures }
          : e
      ));

      // Update in database
      const { data, error: updateError } = await supabase
        .from('entities')
        .update({ features: updatedFeatures })
        .eq('id', entityId)
        .select();

      if (updateError) {
        // Revert optimistic update on error
        loadEntities();
        throw updateError;
      }

      // Check if update actually affected rows (RLS might silently block)
      if (!data || data.length === 0) {
        // Revert optimistic update
        loadEntities();
        throw new Error('Update failed. You may not have permission to modify this entity. Please ensure the RLS policy allows super admin updates.');
      }

      setSuccess(`Feature ${featureName} ${enabled ? 'enabled' : 'disabled'} for ${entity.name}`);
    } catch (err: any) {
      console.error('Error toggling feature:', err);
      setError(err.message || 'Failed to update feature. Please check your permissions.');
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserForm({
      email: '',
      full_name: '',
      role: 'recruiter',
      entity_ids: [],
      is_super_admin: false,
      password: '',
      is_sso_user: false
    });
    setUserDialogOpen(true);
  };

  const handleEditUser = async (user: User) => {
    setEditingUser(user);

    // Fetch user's entity assignments
    let entityIds = user.entity_ids || (user.entity_id ? [user.entity_id] : []);
    try {
      const ueList = await getUserEntities(user.id);
      if (ueList.length > 0) {
        entityIds = ueList.map(ue => ue.entity_id);
      }
    } catch {
      // Fall back to existing data
    }

    setUserForm({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      entity_ids: entityIds,
      is_super_admin: user.is_super_admin,
      password: '', // Don't show existing password
      is_sso_user: false
    });
    setUserDialogOpen(true);
  };

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Full name validation helper
  const isValidFullName = (name: string): boolean => {
    // Name should contain only letters, spaces, hyphens, and apostrophes
    // Should have at least 2 characters and not look like an email
    const nameRegex = /^[a-zA-Z\s'-]{2,}$/;
    return nameRegex.test(name) && !name.includes('@');
  };

  const handleSaveUser = async () => {
    try {
      setError(null);

      // Basic required field validation
      if (!userForm.email || !userForm.full_name || userForm.entity_ids.length === 0) {
        setError('Email, Full Name, and at least one Entity are required');
        return;
      }

      // Trim values for validation
      const email = userForm.email.trim();
      const fullName = userForm.full_name.trim();

      // Validate email format
      if (!isValidEmail(email)) {
        // Check if they might have entered a name in the email field
        if (isValidFullName(email) || !email.includes('@')) {
          setError('Invalid email address. It looks like you may have entered a name in the Email field. Please enter a valid email address (e.g., user@example.com)');
        } else {
          setError('Invalid email format. Please enter a valid email address (e.g., user@example.com)');
        }
        return;
      }

      // Validate full name
      if (!isValidFullName(fullName)) {
        // Check if they might have entered an email in the name field
        if (fullName.includes('@')) {
          setError('Invalid Full Name. It looks like you entered an email address in the Full Name field. Please enter the person\'s name (e.g., John Smith)');
        } else if (fullName.length < 2) {
          setError('Full Name must be at least 2 characters long');
        } else {
          setError('Invalid Full Name. Please enter a valid name using only letters, spaces, hyphens, and apostrophes');
        }
        return;
      }

      // Additional check: warn if email and name seem swapped
      if (isValidEmail(fullName) && isValidFullName(email)) {
        setError('It appears the Email and Full Name values may be swapped. Please check and correct the fields');
        return;
      }

      // Validate email domain for SSO users - must be @gargash.ae
      if (userForm.is_sso_user) {
        const emailDomain = email.toLowerCase().split('@')[1];
        if (emailDomain !== 'gargash.ae') {
          setError('SSO users must have a @gargash.ae email address');
          return;
        }
      }

      if (editingUser) {
        // Update existing user - set primary entity_id to first selected
        const { error: updateError } = await supabase
          .from('users')
          .update({
            name: fullName, // Column is 'name' in database
            role: userForm.role,
            entity_id: userForm.entity_ids[0],
            is_super_admin: userForm.is_super_admin,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingUser.id);

        if (updateError) throw updateError;

        // Update user_entities via API (service role needed for other users' rows)
        try {
          await apiPost('/api/admin', {
            action: 'updateUserEntities',
            userId: editingUser.id,
            entity_ids: userForm.entity_ids,
          });
        } catch (ueErr: any) {
          console.error('Error updating user entities:', ueErr);
          // Non-fatal: user record was updated
        }

        setSuccess('User updated successfully');
        setUserDialogOpen(false);
        loadUsers();
      } else {
        // Create new user
        // Password is required only for non-SSO users
        if (!userForm.is_sso_user && !userForm.password) {
          setError('Password is required for non-SSO users');
          return;
        }

        // Validate password strength for non-SSO users
        if (!userForm.is_sso_user) {
          const passwordValidation = validatePassword(userForm.password);
          if (!passwordValidation.isValid) {
            setError(passwordValidation.errors.join('. '));
            return;
          }
        }

        // Call API endpoint to create user with service role key
        const data = await apiPost('/api/admin', {
          action: 'createUser',
          email: email,
          password: userForm.password,
          full_name: fullName,
          role: userForm.role,
          entity_ids: userForm.entity_ids,
          entity_id: userForm.entity_ids[0],
          is_super_admin: userForm.is_super_admin,
          is_sso_user: userForm.is_sso_user
        });

        setSuccess(data.message || 'User created successfully');
        setUserDialogOpen(false);
        loadUsers();
      }
    } catch (err: any) {
      console.error('Error saving user:', err);
      setError(err.message || 'Failed to save user');
    }
  };

  // Delete Entity
  const handleDeleteEntityClick = (entity: Entity) => {
    setEntityToDelete(entity);
    setDeleteEntityDialogOpen(true);
  };

  const handleConfirmDeleteEntity = async () => {
    if (!entityToDelete) return;

    try {
      setDeleteLoading(true);
      setError(null);

      // Check if entity has users
      const { data: entityUsers, error: usersError } = await supabase
        .from('users')
        .select('id')
        .eq('entity_id', entityToDelete.id);

      if (usersError) throw usersError;

      if (entityUsers && entityUsers.length > 0) {
        setError(`Cannot delete entity "${entityToDelete.name}" because it has ${entityUsers.length} user(s) associated with it. Please reassign or delete the users first.`);
        setDeleteLoading(false);
        return;
      }

      // Delete the entity (CASCADE will handle related data)
      const { error: deleteError } = await supabase
        .from('entities')
        .delete()
        .eq('id', entityToDelete.id);

      if (deleteError) throw deleteError;

      setSuccess(`Entity "${entityToDelete.name}" deleted successfully`);
      setDeleteEntityDialogOpen(false);
      setEntityToDelete(null);
      loadEntities();
    } catch (err: any) {
      console.error('Error deleting entity:', err);
      setError(err.message || 'Failed to delete entity');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Delete User
  const handleDeleteUserClick = (user: User) => {
    setUserToDelete(user);
    setDeleteUserDialogOpen(true);
  };

  const handleConfirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleteLoading(true);
      setError(null);

      // Get current user to prevent self-deletion
      const currentUser = await getAuthUser();
      if (currentUser && currentUser.id === userToDelete.id) {
        setError('You cannot delete your own account');
        setDeleteLoading(false);
        return;
      }

      // Call API to delete user (handles both auth and database)
      const data = await apiPost('/api/admin', {
        action: 'deleteUser',
        userId: userToDelete.id
      });

      setSuccess(data.message || `User "${userToDelete.full_name}" deleted successfully`);
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (!isSuperAdmin) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>Access Denied</Typography>
          {error || 'You do not have super admin privileges to access this page.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          p: 3,
          mb: 3
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              bgcolor: '#ff5252',
              width: 48,
              height: 48
            }}
          >
            <ShieldIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Super Admin Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage entities and users across the platform
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Platform Statistics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(8, 1fr)'
          },
          gap: 2,
          mb: 3
        }}
      >
        {/* Entities */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.03)
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), width: 40, height: 40 }}>
              <BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {entities.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Entities
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Users */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.03)
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), width: 40, height: 40 }}>
              <PersonIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="success.main">
                {users.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Users
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Total Jobs */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.03)
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), width: 40, height: 40 }}>
              <WorkIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="info.main">
                {platformStats.totalJobs}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Jobs
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Active Jobs */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.03)
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), width: 40, height: 40 }}>
              <WorkIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="success.main">
                {platformStats.activeJobs}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active Jobs
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Total Applications */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.03)
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), width: 40, height: 40 }}>
              <DescriptionIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="warning.main">
                {platformStats.totalApplications}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Applications
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* New Applications (7 days) */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.secondary.main, 0.03)
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), width: 40, height: 40 }}>
              <FiberNewIcon sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="secondary.main">
                {platformStats.newApplications}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                New (7 days)
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Conversion Rate */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.03)
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), width: 40, height: 40 }}>
              <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="success.main">
                {platformStats.conversionRate}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Hired Rate
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Avg Days to Hire */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.03)
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), width: 40, height: 40 }}>
              <ScheduleIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="info.main">
                {platformStats.avgDaysToHire}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg Days
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            '& .MuiTab-root': { fontWeight: 600 }
          }}
        >
          <Tab label={`Entities (${entities.length})`} />
          <Tab label={`Users (${users.length})`} />
          <Tab label="Feature Management" />
        </Tabs>

        {/* Entities Tab */}
        {currentTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Entity Management
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateEntity}
              >
                Create Entity
              </Button>
            </Stack>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Subdomain</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entities.map((entity) => (
                    <TableRow key={entity.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography fontWeight={600}>{entity.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{entity.subdomain}</TableCell>
                      <TableCell>
                        <Chip
                          label={entity.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={entity.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(entity.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditEntity(entity)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteEntityClick(entity)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Users Tab */}
        {currentTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                User Management
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateUser}
              >
                Add User
              </Button>
            </Stack>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Entity</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Super Admin</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PersonIcon fontSize="small" color="action" />
                          <Box>
                            <Typography fontWeight={600}>{user.full_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {user.entity_names && user.entity_names.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {user.entity_names.map((name: string, idx: number) => (
                              <Chip
                                key={idx}
                                label={name}
                                size="small"
                                variant={user.entity_ids?.[idx] === user.entity_id ? 'filled' : 'outlined'}
                                color="primary"
                                sx={{ mb: 0.5 }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          user.entity_name || 'Unknown'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={user.role} size="small" />
                      </TableCell>
                      <TableCell>
                        {user.is_super_admin && (
                          <Chip
                            label="Super Admin"
                            size="small"
                            color="error"
                            icon={<ShieldIcon />}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditUser(user)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteUserClick(user)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Features Tab */}
        {currentTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Typography variant="h6" fontWeight={600}>
                Entity Feature Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enable or disable features for each entity. Changes take effect immediately.
              </Typography>

              {entities.map((entity) => (
                <Paper
                  key={entity.id}
                  elevation={0}
                  sx={{
                    p: 3,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: 2
                  }}
                >
                  <Stack spacing={2}>
                    {/* Entity Header */}
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main
                        }}
                      >
                        <BusinessIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {entity.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entity.subdomain}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Feature Toggles */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                        gap: 2,
                        mt: 2
                      }}
                    >
                      {/* Resume Screening */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Resume Screening
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            AI-powered resume analysis
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => handleToggleFeature(entity.id, 'resumeScreening', !entity.features?.resumeScreening)}
                          color={entity.features?.resumeScreening ? 'success' : 'default'}
                        >
                          {entity.features?.resumeScreening ? <ToggleOnIcon fontSize="large" /> : <ToggleOffIcon fontSize="large" />}
                        </IconButton>
                      </Stack>

                      {/* Analytics */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Analytics
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Advanced analytics dashboard
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => handleToggleFeature(entity.id, 'analytics', !entity.features?.analytics)}
                          color={entity.features?.analytics ? 'success' : 'default'}
                        >
                          {entity.features?.analytics ? <ToggleOnIcon fontSize="large" /> : <ToggleOffIcon fontSize="large" />}
                        </IconButton>
                      </Stack>

                      {/* Call Initiation */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Call Initiation
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            AI call screening
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => handleToggleFeature(entity.id, 'callInitiation', !entity.features?.callInitiation)}
                          color={entity.features?.callInitiation ? 'success' : 'default'}
                        >
                          {entity.features?.callInitiation ? <ToggleOnIcon fontSize="large" /> : <ToggleOffIcon fontSize="large" />}
                        </IconButton>
                      </Stack>

                      {/* Custom Themes */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Custom Themes
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Custom branding and colors
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => handleToggleFeature(entity.id, 'customThemes', !entity.features?.customThemes)}
                          color={entity.features?.customThemes ? 'success' : 'default'}
                        >
                          {entity.features?.customThemes ? <ToggleOnIcon fontSize="large" /> : <ToggleOffIcon fontSize="large" />}
                        </IconButton>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Entity Dialog */}
      <Dialog open={entityDialogOpen} onClose={() => setEntityDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEntity ? 'Edit Entity' : 'Create New Entity'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              label="Entity Name"
              fullWidth
              value={entityForm.name}
              onChange={(e) => setEntityForm({ ...entityForm, name: e.target.value })}
              required
            />
            <TextField
              label="Subdomain"
              fullWidth
              value={entityForm.subdomain}
              onChange={(e) => setEntityForm({ ...entityForm, subdomain: e.target.value })}
              helperText="e.g., 'acmecorp' for acmecorp.hrportal.com"
              required
            />
            <TextField
              label="Logo URL"
              fullWidth
              value={entityForm.logo}
              onChange={(e) => setEntityForm({ ...entityForm, logo: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEntityDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEntity}>
            {editingEntity ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Create New User'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              disabled={!!editingUser}
              required
            />
            <TextField
              label="Full Name"
              fullWidth
              value={userForm.full_name}
              onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
              required
            />
            {!editingUser && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={userForm.is_sso_user}
                      onChange={(e) => setUserForm({ ...userForm, is_sso_user: e.target.checked, password: e.target.checked ? '' : userForm.password })}
                      color="primary"
                    />
                  }
                  label="Microsoft Azure SSO User"
                />
                {!userForm.is_sso_user && (
                  <TextField
                    label="Password"
                    type="password"
                    fullWidth
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    required
                    helperText={`Requirements: ${getPasswordRequirements().join('; ')}`}
                    multiline
                    FormHelperTextProps={{ sx: { whiteSpace: 'pre-wrap' } }}
                  />
                )}
                {userForm.is_sso_user && (
                  <Alert severity="info">
                    This user will authenticate via Microsoft Azure AD. No password is required.
                  </Alert>
                )}
              </>
            )}
            <FormControl fullWidth required>
              <InputLabel>Entities</InputLabel>
              <Select
                multiple
                value={userForm.entity_ids}
                onChange={(e) => {
                  const value = e.target.value;
                  setUserForm({
                    ...userForm,
                    entity_ids: typeof value === 'string' ? value.split(',') : value,
                  });
                }}
                input={<OutlinedInput label="Entities" />}
                renderValue={(selected) =>
                  (selected as string[])
                    .map(id => entities.find(e => e.id === id)?.name || id)
                    .join(', ')
                }
              >
                {entities.map((entity) => (
                  <MenuItem key={entity.id} value={entity.id}>
                    <Checkbox checked={userForm.entity_ids.indexOf(entity.id) > -1} />
                    <ListItemText primary={entity.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                label="Role"
              >
                <MenuItem value="recruiter">Recruiter</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Super Admin</InputLabel>
              <Select
                value={userForm.is_super_admin ? 'yes' : 'no'}
                onChange={(e) => setUserForm({ ...userForm, is_super_admin: e.target.value === 'yes' })}
                label="Super Admin"
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveUser}>
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Entity Confirmation Dialog */}
      <Dialog
        open={deleteEntityDialogOpen}
        onClose={() => !deleteLoading && setDeleteEntityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Delete Entity
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Typography>
              Are you sure you want to delete the entity <strong>"{entityToDelete?.name}"</strong>?
            </Typography>
            <Alert severity="warning">
              <Typography variant="body2">
                This action cannot be undone. All associated data (jobs, applications, bulk uploads, etc.) will be permanently deleted.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteEntityDialogOpen(false)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteEntity}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Entity'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteUserDialogOpen}
        onClose={() => !deleteLoading && setDeleteUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Delete User
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Typography>
              Are you sure you want to delete the user <strong>"{userToDelete?.full_name}"</strong> ({userToDelete?.email})?
            </Typography>
            <Alert severity="warning">
              <Typography variant="body2">
                This action cannot be undone. The user will be removed from the authentication system and all their data will be permanently deleted.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteUserDialogOpen(false)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteUser}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
